import { useMemo, useCallback } from 'react';
import {
  Body1Strong,
  Subtitle1,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  Button,
} from '@fluentui/react-components';
import { DeleteRegular, AddFilled } from '@fluentui/react-icons';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Avatar from '../components/Avatar';
import { TUser } from '../types/users.types';
import { useCreate, useDeactivate, useUpdate, useUsers } from '../api/users.api';
import { useOrganization } from '../api/organizations.api';
import { ControlledField, ConfirmMenu, StatusLabel, Bubble } from '@shared/components';
import { useDocumentTitle } from '@shared/hooks';
import UserGroupsDropdown from '../components/UserGroupsDropdown';
import UserLicensesMenu from '../components/UserLicensesMenu';

const schema = yup.object({
  mail: yup.string().required().email(),
});
type FormData = yup.InferType<typeof schema>;

const useStyles = makeStyles({
  mailInput: {
    width: '300px',
  },
  dropdown: {
    minWidth: 0,
    width: '97%',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    minWidth: '1080px',
  },
  licenseColumn: {
    width: '240px',
  },
  actionsColumn: {
    width: '80px',
    textAlign: 'center',
  },
});

const UserManagement = () => {
  const { users, error } = useUsers();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDeactivate();
  const organization = useOrganization();

  const data = useMemo(() => (users && organization ? { users, organization } : null), [users, organization]);
  const assignedAiLicenses = useMemo(() => users?.filter((user) => user.hasAiLicense).length ?? 0, [users]);

  const getHandleDelete = (user: TUser) => () => {
    deleteMutation.mutate({ id: user._id });
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { mail: '' },
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      createMutation.mutate(data, {
        onSuccess: () => {
          reset();
        },
      });
    },
    [reset, createMutation]
  );

  useDocumentTitle('User management');

  const classes = useStyles();

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading users..."
      error={error}
      errorMessage="Loading users failed!"
      render={(resolvedData) => (
        <div className="max-w-7xl">
          <p className="pb-4">
            <Subtitle1>User management</Subtitle1>
          </p>
          <form onSubmit={handleSubmit(onSubmit)} className="mb-1 flex space-x-3">
            <ControlledField
              label="Mail"
              name="mail"
              control={control}
              errors={errors}
              fixedHeight
              required
              className={classes.mailInput}
            />
            <div className="mt-6 pt-0.5">
              <Button type="submit" appearance="primary" disabled={createMutation.isPending} icon={<AddFilled />}>
                Invite user
              </Button>
            </div>
          </form>

          <Bubble>
            <div className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell key="name">
                      <Body1Strong>Name</Body1Strong>
                    </TableHeaderCell>
                    <TableHeaderCell key="mail">
                      <Body1Strong>Mail address</Body1Strong>
                    </TableHeaderCell>
                    <TableHeaderCell key="groups">
                      <Body1Strong>Groups</Body1Strong>
                    </TableHeaderCell>
                    <TableHeaderCell key="licenses" className={classes.licenseColumn}>
                      <Body1Strong>Licenses</Body1Strong>
                    </TableHeaderCell>
                    <TableHeaderCell key="actions" className={classes.actionsColumn}>
                      <Body1Strong>Actions</Body1Strong>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resolvedData.users.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <TableCellLayout media={<Avatar user={user} />}>
                          {user.name || 'Sent invitation...'}
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <span className="block max-w-full text-ellipsis overflow-hidden">{user.mail}</span>
                      </TableCell>
                      <TableCell>
                        <UserGroupsDropdown
                          user={user}
                          org={resolvedData.organization}
                          className={classes.dropdown}
                          onChange={(update) => updateMutation.mutate({ id: user._id, update })}
                        />
                      </TableCell>
                      <TableCell className={classes.licenseColumn}>
                        <div className={classes.dropdown}>
                          <UserLicensesMenu
                            user={user}
                            isAiLicenseLimitReached={assignedAiLicenses >= resolvedData.organization.license.AI}
                            isUpdating={updateMutation.isPending}
                            onChange={(update) => updateMutation.mutate({ id: user._id, update })}
                          />
                        </div>
                      </TableCell>
                      <TableCell className={classes.actionsColumn}>
                        <ConfirmMenu onConfirm={getHandleDelete(user)}>
                          <Button icon={<DeleteRegular />} aria-label="Delete" />
                        </ConfirmMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Bubble>
        </div>
      )}
    />
  );
};

export default UserManagement;
